import { useEffect, useMemo, useRef, useState } from "react";
import aboutFieldFun from "../assets/img/about-field-fun.png";
import { AboutSky } from "./AboutSky";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TODAY_MOVE = {
  Sunday: "Protect a slow morning",
  Monday: "Write the problem first",
  Tuesday: "Sit with a customer quote",
  Wednesday: "Cut one meeting",
  Thursday: "Ship something before tomorrow",
  Friday: "Write down what actually moved",
  Saturday: "Cook something that takes time",
};

const notes = [
  {
    text: (
      <>
        The numbers were always there.
        <em> Now they land.</em>
      </>
    ),
    cite: "a product note",
  },
  {
    text: (
      <>
        Ship a thin slice,
        <em> not a deck.</em>
      </>
    ),
    cite: "a working rule",
  },
  {
    text: (
      <>
        If they can’t tell the story,
        <em> it isn’t done.</em>
      </>
    ),
    cite: "a launch test",
  },
  {
    text: (
      <>
        Clarity is a product decision.
        <em> So is cutting.</em>
      </>
    ),
    cite: "a weekly reminder",
  },
];

const zine = [
  {
    kicker: "Note",
    title: "Overlapping worlds of AI product & data",
    body: "Tap through a tiny zine: three pages on how I actually like to build.",
    cta: "Read",
  },
  {
    kicker: "01",
    title: "One loop, not two jobs",
    body: "AI product and data aren’t parallel tracks. The model is only useful if the workflow around it ships.",
    cta: "Next",
  },
  {
    kicker: "02",
    title: "The workflow is the product",
    body: "People don’t adopt a model. They adopt a faster path from question to answer they can trust.",
    cta: "Next",
  },
  {
    kicker: "03",
    title: "Measure the unglamorous bit",
    body: "Insight to shipped software is messy. I stay until the tenth customer has the same path as the first.",
    cta: "Again",
  },
];

const chips = ["250+", "$1M", "30%", "ship", "slice"];

const oracles = [
  "Ship a thinner slice.",
  "Not until the tenth user can.",
  "Yes, if you can measure it.",
  "Cut the deck. Keep the loop.",
  "Ask a customer, not the room.",
  "It's a workflow, not a model.",
  "If they can't tell the story, it isn't done.",
  "Start with retrieval, not the model.",
  "Kill the metric that doesn't move.",
  "Sleep on it. Then ship at 9.",
];

const WeekendCard = () => {
  const dayIndex = new Date().getDay();
  const day = WEEKDAYS[dayIndex];
  const items = useMemo(
    () => [
      { t: "Ship a thin slice, not a deck", done: true },
      { t: TODAY_MOVE[day], done: false },
      { t: "Walk without a podcast", done: false },
      { t: "Read one PR like a user would", done: false },
      { t: "Text someone I owe a note", done: dayIndex === 0 || dayIndex === 6 },
    ],
    [day, dayIndex]
  );
  const [checks, setChecks] = useState(() => items.map((item) => item.done));
  const done = checks.filter(Boolean).length;
  const cleared = done === items.length;
  const weekend = dayIndex === 0 || dayIndex === 6;

  return (
    <div className={`ff-card ff-wk pos-lt ${cleared ? "is-cleared" : ""}`}>
      <span className="ff-wk-day">{day}</span>
      <span className="ff-wk-dots" aria-hidden="true">
        {WEEKDAYS.map((name, i) => (
          <i key={name} className={i === dayIndex ? "is-today" : ""} />
        ))}
      </span>
      <div className="ff-wk-panel">
        <p className="ff-wk-title">{cleared ? "Shipped the day." : weekend ? "Weekend To do" : `${day} To do`}</p>
        <div className="ff-wk-list">
          {items.map((item, i) => (
            <button
              type="button"
              className={`r ${checks[i] ? "is-on" : ""}`}
              key={item.t}
              onClick={() =>
                setChecks((curr) => curr.map((v, idx) => (idx === i ? !v : v)))
              }
            >
              <span className="cb" />
              <span className="t">{item.t}</span>
            </button>
          ))}
        </div>
        <span className="ff-wk-count">
          {done}/{items.length}
        </span>
      </div>
    </div>
  );
};

const QuoteCard = () => {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [snap, setSnap] = useState(false);
  const lock = useRef(false);
  const front = notes[index];
  const back = notes[(index + 1) % notes.length];

  const flip = () => {
    if (lock.current) return;
    lock.current = true;
    setFlipped(true);
  };

  const settle = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName && e.propertyName !== "transform") return;
    if (!flipped) return;
    setSnap(true);
    setIndex((i) => (i + 1) % notes.length);
    setFlipped(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setSnap(false);
        lock.current = false;
      });
    });
  };

  return (
    <div
      className={`ff-card ff-quote pos-ct ${flipped ? "is-flipped" : ""} ${snap ? "is-snap" : ""}`}
      onClick={flip}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          flip();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Flip to the next product note"
    >
      <div className="ff-quote-flip" onTransitionEnd={settle}>
        <div className="ff-quote-face">
          <p className="ff-quote-text">{front.text}</p>
          <p className="ff-quote-cite">– {front.cite}</p>
          <span className="ff-hint">Tap to flip {index + 1}/{notes.length}</span>
        </div>
        <div className="ff-quote-face ff-quote-back">
          <p className="ff-quote-text">{back.text}</p>
          <p className="ff-quote-cite">– {back.cite}</p>
          <span className="ff-hint">Tap to flip</span>
        </div>
      </div>
    </div>
  );
};

const VinylCard = ({ musicOn, onToggleMusic }) => (
  <div
    className={`ff-card ff-gully pos-rt ${musicOn ? "is-playing" : ""}`}
    onClick={onToggleMusic}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggleMusic();
      }
    }}
    role="button"
    tabIndex={0}
    aria-pressed={musicOn}
    aria-label={musicOn ? "Pause Night on the Docks" : "Play Night on the Docks"}
  >
    <div className="ff-vinyl" aria-hidden="true">
      <span className="ff-vinyl-label" />
    </div>
    <span className="ff-arm" aria-hidden="true" />
    <div className="ff-eq" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
      <i />
    </div>
    <p className="ff-gully-a">
      A song that never gets <em>old…</em>
    </p>
    <p className="ff-gully-b">{musicOn ? "Now playing: Night on the Docks" : "Night field · tap to play"}</p>
    <div className="ff-gully-bar">
      <span />
    </div>
    <span className="ff-gully-play">{musicOn ? "Pause" : "Play"}</span>
  </div>
);

const ZineCard = () => {
  const [page, setPage] = useState(0);
  const current = zine[page];
  const next = () => setPage((n) => (n + 1) % zine.length);
  return (
    <div
      className={`ff-card ff-blog pos-lb ${page > 0 ? "is-open" : ""}`}
      onClick={next}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          next();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Read the next zine page"
    >
      <p className="ff-blog-kicker">{current.kicker}</p>
      <h3>{current.title}</h3>
      <p className="ff-blog-body">{current.body}</p>
      <span className="ff-blog-cta">{current.cta}</span>
      <span className="ff-hint">{page + 1}/{zine.length}</span>
    </div>
  );
};

const OracleCard = () => {
  const [angle, setAngle] = useState(-28);
  const [spinning, setSpinning] = useState(false);
  const [answer, setAnswer] = useState("");
  const timer = useRef(0);
  const last = useRef(-1);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const ask = () => {
    if (spinning) return;
    let next = Math.floor(Math.random() * oracles.length);
    if (next === last.current) next = (next + 1) % oracles.length;
    last.current = next;
    const turns = 3 + Math.floor(Math.random() * 3);
    const landing = next * (360 / oracles.length) + (Math.random() * 18 - 9);
    setSpinning(true);
    setAnswer("");
    setAngle((curr) => {
      const base = ((curr % 360) + 360) % 360;
      return curr + turns * 360 + landing - base;
    });
    timer.current = window.setTimeout(() => {
      setAnswer(oracles[next]);
      setSpinning(false);
    }, 1400);
  };

  return (
    <div
      className={`ff-card ff-oracle pos-cb ${spinning ? "is-spinning" : ""} ${answer ? "has-answer" : ""}`}
      onClick={ask}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          ask();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Ask the field whether to ship it"
    >
      <p className="ff-oracle-kicker">{spinning ? "Searching…" : "Ask the field"}</p>
      <div className="ff-oracle-dial" aria-hidden="true">
        <span className="ff-oracle-ring" />
        <span className="ff-oracle-nodes">
          {Array.from({ length: 8 }).map((_, i) => (
            <i key={i} style={{ "--i": i }} />
          ))}
        </span>
        <span className="ff-oracle-needle" style={{ transform: `rotate(${angle}deg)` }} />
        <span className="ff-oracle-hub" />
      </div>
      <p className="ff-oracle-q">Should we ship it?</p>
      <p className="ff-oracle-a">{answer || "Tap the dial."}</p>
    </div>
  );
};

const ShipGame = () => {
  const [phase, setPhase] = useState("idle");
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [lives, setLives] = useState(3);
  const [bits, setBits] = useState([]);
  const livesRef = useRef(3);
  const scoreRef = useRef(0);

  useEffect(() => {
    livesRef.current = lives;
  }, [lives]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    if (phase !== "play") return undefined;
    const spawn = () => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const label = chips[Math.floor(Math.random() * chips.length)];
      const x = 8 + Math.random() * 64;
      const dur = Math.max(2.6, 4.4 - scoreRef.current * 0.08);
      setBits((curr) => (curr.length > 5 ? curr : [...curr, { id, label, x, dur }]));
    };
    const kick = window.setTimeout(spawn, 280);
    const timer = window.setInterval(spawn, 1150);
    return () => {
      window.clearTimeout(kick);
      window.clearInterval(timer);
    };
  }, [phase]);

  const endGame = () => {
    setPhase("over");
    setBits([]);
    setBest((n) => Math.max(n, scoreRef.current));
  };

  const catchBit = (id) => {
    if (phase !== "play") return;
    setBits((curr) => curr.filter((bit) => bit.id !== id));
    setScore((n) => n + 1);
  };

  const missBit = (id) => {
    setBits((curr) => curr.filter((bit) => bit.id !== id));
    if (phase !== "play") return;
    const next = livesRef.current - 1;
    livesRef.current = next;
    setLives(next);
    if (next <= 0) endGame();
  };

  const start = () => {
    livesRef.current = 3;
    scoreRef.current = 0;
    setLives(3);
    setScore(0);
    setBits([]);
    setPhase("play");
  };

  return (
    <div className={`ff-card ff-play pos-rb ${phase === "play" ? "is-hot" : ""}`}>
      <div className="ff-play-top">
        <p>{phase === "play" ? "Catch what ships." : "Recently shipped a tiny habit."}</p>
        <strong>{phase === "idle" ? `best ${best}` : phase === "play" ? `+${score}` : `shipped ${score}`}</strong>
      </div>
      <div className="ff-play-field" aria-hidden={phase !== "play"}>
        {phase === "play" &&
          bits.map((bit) => (
            <button
              key={bit.id}
              type="button"
              className="ff-bit"
              style={{ left: `${bit.x}%`, animationDuration: `${bit.dur}s` }}
              onClick={(e) => {
                e.stopPropagation();
                catchBit(bit.id);
              }}
              onAnimationEnd={() => missBit(bit.id)}
            >
              {bit.label}
            </button>
          ))}
        {phase === "play" && (
          <div className="ff-lives" aria-label={`${lives} lives left`}>
            {Array.from({ length: 3 }).map((_, i) => (
              <i key={i} className={i < lives ? "on" : ""} />
            ))}
          </div>
        )}
        {phase !== "play" && (
          <p className="ff-play-idle">
            {phase === "over" ? "Three dropped. Ship another round?" : "Catch the chips before they fall."}
          </p>
        )}
      </div>
      {phase !== "play" && (
        <button type="button" onClick={start}>
          {phase === "over" ? "Again" : "PLAY"}
        </button>
      )}
    </div>
  );
};

export const FunFacts = ({ musicOn, onToggleMusic }) => {
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduce) return undefined;
    let last = null;
    const clear = (card) => {
      card?.style.setProperty("--tilt-x", "0deg");
      card?.style.setProperty("--tilt-y", "0deg");
    };
    const onMove = (e) => {
      const card = e.target.closest(".ff-card");
      if (last && last !== card) clear(last);
      last = card;
      if (!card || card.classList.contains("is-hot") || card.classList.contains("is-spinning")) return;
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      card.style.setProperty("--tilt-x", `${(-py * 7).toFixed(2)}deg`);
      card.style.setProperty("--tilt-y", `${(px * 9).toFixed(2)}deg`);
    };
    const onLeave = () => {
      clear(last);
      last = null;
    };
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerleave", onLeave);
    return () => {
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <>
      <section className="about-fun" id="fun">
        <div className="stack-lid" aria-hidden="true" />
        <div className="aff-bg" aria-hidden="true">
          <div
            className="about-field about-field--fun"
            style={{ backgroundImage: `url(${aboutFieldFun})` }}
          />
          <AboutSky tone="fun" />
        </div>
        <div className="about-fun-stage" ref={stageRef}>
          <WeekendCard />
          <QuoteCard />
          <VinylCard musicOn={musicOn} onToggleMusic={onToggleMusic} />
          <ZineCard />
          <OracleCard />
          <ShipGame />
        </div>
      </section>
      <div className="aff-hold" aria-hidden="true" />
    </>
  );
};
